import * as XLSX from "xlsx";
import { message } from "antd";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDate } from "./formatters";

declare module "jspdf" {
  interface jsPDF {
    lastAutoTable: {
      finalY?: number;
      [key: string]: unknown;
    };
  }
}

type ExcelData = {
  [key: string]: string; // dynamic key-value pair for any columns
};

export function handleExportToExcel(
  data: any[],
  columns: any[],
  fileName: string
) {
  if (!data || data?.length === 0 || !columns || columns?.length === 0) {
    message.warning("No data or columns provided for export.");
    return;
  }

  // Extract column headers
  const headers = columns.map((col) => col.dataIndex);
  const headerLabels = columns.map((col) => col.title);

  // Convert data to a format suitable for Excel
  const sheetData = [
    headerLabels, // First row: column titles
    ...data.map((row) =>
      headers.map((key, index) => {
        const column = columns[index];
        // If column has a render function, apply it and extract the plain text
        if (column.render) {
          const rendered: any =
            typeof column.render === "function"
              ? column.render(row[key], row)
              : row[key];
          return rendered?.props?.children ?? rendered ?? row[key] ?? "";
        }
        return row[key] ?? "";
      })
    ),
  ];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  // Auto-adjust column widths based on content length
  const columnWidths = headers.map((col, index) => ({
    wch: Math.max(
      headerLabels[index].length,
      ...data.map((row) => (row[col] ? String(row[col]).length : 0))
    ),
  }));
  worksheet["!cols"] = columnWidths;

  // Create and save workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

export function handleExportToExcelWithFilters(
  data: any[],
  columns: any[],
  fileName: string,
  filters?: Record<string, any>
) {
  if (!data || data?.length === 0 || !columns || columns?.length === 0) {
    message.warning("No data or columns provided for export.");
    return;
  }

  const headers = columns.map((col) => col.dataIndex);
  const headerLabels = columns.map((col) => col.title);

  // Convert data rows
  const dataRows = data.map((row) =>
    headers.map((key, index) => {
      const column = columns[index];
      if (column.render && typeof column.render === "function") {
        const rendered: any = column.render(row[key], row);
        return rendered?.props?.children ?? rendered ?? row[key] ?? "";
      }
      return row[key] ?? "";
    })
  );

  // Convert filters to row format (e.g. "Branch ID: 123")
  const filterRows: any[][] = filters
    ? Object.entries(filters).map(([key, value]) => [
        key.replace(/_/g, " ").toUpperCase(),
        value || "-",
      ])
    : [];

  if (filterRows.length > 0) {
    filterRows.push([]); // Empty row separator
  }

  // Combine all together
  const sheetData = [...filterRows, headerLabels, ...dataRows];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  const columnWidths = headers.map((col, index) => ({
    wch: Math.max(
      headerLabels[index]?.length ?? 10,
      ...data.map((row) => (row[col] ? String(row[col]).length : 0))
    ),
  }));
  worksheet["!cols"] = columnWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

export function handleExportToPDF(
  data: any[],
  columns: any[],
  fileName: string,
  headerInfo?: { title: string; content: string },
  footerText?: string
) {
  if (!data?.length || !columns?.length) {
    console.warn("No data or columns for PDF export.");
    return;
  }

  const doc = new jsPDF();

  // Header
  if (headerInfo) {
    doc.setFontSize(18);
    doc.text(headerInfo.title, 14, 22);
    doc.setFontSize(10);
    const pageWidth = doc.internal.pageSize.getWidth();
    const wrapped = doc.splitTextToSize(headerInfo.content, pageWidth - 35);
    doc.text(wrapped, 14, 30);
  }

  // Prepare table
  autoTable(doc, {
    head: [columns.map((col) => col.title)],
    body: data.map((row) =>
      columns.map((col) => {
        const val = row[col.dataIndex];
        return typeof val === "string" || typeof val === "number" ? val : "";
      })
    ),
    startY: 40,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [79, 104, 141] },
    theme: "grid",
  });

  // Footer
  if (footerText) {
    const lastY = doc.lastAutoTable.finalY || 50;
    doc.text(footerText, 14, lastY + 10);
  }

  doc.save(fileName);
}

export function handleExportToPDFWithFilters(
  data: any[],
  columns: any[],
  fileName: string,
  headerInfo?: { title: string; content: string },
  footerText?: string
) {
  if (!data?.length || !columns?.length) {
    console.warn("No data or columns for PDF export.");
    return;
  }

  const doc = new jsPDF();
  let currentY = 22;

  // Header
  if (headerInfo) {
    doc.setFontSize(18);
    doc.text(headerInfo.title, 14, currentY);
    currentY += 8;

    doc.setFontSize(10);
    const lines = headerInfo.content.split("\n");

    lines.forEach((line) => {
      const [label, value] = line.split(": ");
      if (label && value !== undefined) {
        doc.setFont("helvetica", "normal");
        doc.text(`${label}:`, 14, currentY);
        doc.setFont("helvetica", "normal");
        doc.text(` ${value}`, doc.getTextWidth(`${label}:`) + 14, currentY);
      } else {
        doc.setFont("helvetica", "normal");
        doc.text(line, 14, currentY);
      }
      currentY += 6; // vertical spacing between lines
    });

    doc.setFont("helvetica", "normal"); // reset
  }

  // Prepare table below header block
  autoTable(doc, {
    head: [columns.map((col) => col.title)],
    body: data.map((row) =>
      columns.map((col) => {
        const val = row[col.dataIndex];
        return typeof val === "string" || typeof val === "number" ? val : "";
      })
    ),
    startY: currentY + 4, // dynamically set Y position below header
    styles: { fontSize: 8 },
    headStyles: { fillColor: [79, 104, 141] },
    theme: "grid",
  });

  // Footer
  if (footerText) {
    const lastY = doc.lastAutoTable.finalY || currentY + 50;
    doc.text(footerText, 14, lastY + 10);
  }

  doc.save(fileName);
}

const validateExcelHeaders = (headers: string[], expectedHeaders: string[]) => {
  const missingHeaders = expectedHeaders.filter(
    (header) => !headers.includes(header)
  );
  return missingHeaders;
};

export interface ExpectedHeader {
  label: string;
  value: string;
  type?: "date" | string;
}

export const processExcelFile = (
  file: File,
  expectedHeaders: ExpectedHeader[],
  onDataProcessed: (data: ExcelData[]) => void
) => {
  if (file) {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: "binary" });

      // Assuming we're working with the first sheet
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      // Convert sheet data to JSON
      const sheetData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
      }) as string[][];

      // Extract headers
      const headers = sheetData[0];
      const missingHeaders = validateExcelHeaders(
        headers,
        expectedHeaders.map((header) => header.label)
      );

      if (missingHeaders.length > 0) {
        message.warning(
          `Invalid file format! Missing headers: ${missingHeaders.join(", ")}`
        );
        return;
      }

      // Extract and format data rows dynamically based on the expected headers
      const dataRows = sheetData.slice(1).map((row) => {
        const rowData: ExcelData = {};

        expectedHeaders.forEach((header) => {
          const headerIndex = headers.indexOf(header.label);
          if (headerIndex !== -1) {
            const cellValue = row[headerIndex];

            if (header.type === "date") {
              const formattedDate = formatDate(
                cellValue,
                "YYYY-MM-DD HH:mm:ss"
              );
              // const formattedDate = cellValue;
              if (!formattedDate) {
                message.warning(
                  `Invalid date format in column: ${header.label}`
                );
              }
              rowData[header.value] = formattedDate || cellValue;
            } else {
              rowData[header.value] = cellValue;
            }
          }
        });
        return rowData;
      });

      // Call the callback with the processed data
      onDataProcessed(dataRows);
    };

    // Read the file as binary string
    reader.readAsBinaryString(file);
  }
};
